import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import capitalize from 'lodash.capitalize';
import type { IObservableArray } from 'mobx';
import type { JSX } from 'react';
import React from 'react';

import type { Campaign, Creative, DailyStat, DailyStats } from '@feathr/blackbox';
import { CreativeClass } from '@feathr/blackbox';
import { StatusPillCell, Text, TruncatedTextCell } from '@feathr/components';

import CreativeStat from './CreativeStat';
import CreativeThumbnail from './CreativeThumbnail';

function getSpecLabel(creative: Creative): string {
  if (creative.get('spec')) {
    return creative.get('spec').split('_').map(capitalize).join(' ');
  }
  switch (creative.get('_cls')) {
    case CreativeClass.ReferralPage:
      return 'Page';

    case CreativeClass.ReferralBanner:
      return 'Banner';

    case CreativeClass.ReferralEmail:
      return 'Email';

    default:
      return '-';
  }
}

function getDestinationUrl(campaign: Campaign, creative: Creative): string {
  return creative.get('destination_url') || campaign.get('destination_url');
}

function calculateViews(dailyStats: IObservableArray<DailyStat>): number {
  return dailyStats.reduce((acc, stat) => acc + (stat.get('num_views') || 0), 0);
}

function calculateClicks(dailyStats: IObservableArray<DailyStat>): number {
  return dailyStats.reduce((acc, stat) => acc + (stat.get('num_clicks') || 0), 0);
}

function calculateCtr(dailyStats: IObservableArray<DailyStat>): number {
  return calculateClicks(dailyStats) / calculateViews(dailyStats);
}

function CreativeTableColumns(
  campaign: Campaign,
  start: string,
  end: string,
  Stats: DailyStats,
  t: TFunction,
): Array<ColumnDef<Creative>> {
  return [
    {
      id: 'thumbnail',
      header: t('Thumbnail'),
      size: 210,
      enableSorting: false,
      meta: { align: 'center' },
      cell({ row }: CellContext<Creative, unknown>): JSX.Element {
        return <CreativeThumbnail creative={row.original} />;
      },
    },
    {
      id: 'name',
      accessorFn: (row): string => row.get('name') ?? '',
      header: t('Name'),
      sortDescFirst: false,
      cell({ row }: CellContext<Creative, unknown>): JSX.Element {
        return <TruncatedTextCell value={row.original.get('name') || t('Unnamed Creative')} />;
      },
    },
    {
      id: 'spec',
      accessorFn: (row): string => getSpecLabel(row),
      header: t('Spec'),
      size: 150,
      sortDescFirst: false,
      cell({ row }: CellContext<Creative, unknown>): JSX.Element {
        return <Text.DefaultRegular>{getSpecLabel(row.original)}</Text.DefaultRegular>;
      },
    },
    {
      id: 'dimensions',
      header: t('Dimensions'),
      size: 110,
      enableSorting: false,
      meta: { align: 'right' },
      cell({ row }: CellContext<Creative, unknown>): JSX.Element {
        const { original } = row;
        if (original.get('width') && original.get('height')) {
          return (
            <Text.DefaultRegular>
              {`${original.get('width')}px x ${original.get('height')}px`}
            </Text.DefaultRegular>
          );
        }
        return <Text.DefaultRegular>-</Text.DefaultRegular>;
      },
    },
    {
      id: 'views',
      header: t('Views'),
      size: 110,
      enableSorting: false,
      meta: { align: 'right' },
      cell({ row }: CellContext<Creative, unknown>): JSX.Element {
        return (
          <CreativeStat
            calculate={calculateViews}
            creative={row.original}
            end={end}
            format={'count'}
            start={start}
            Stats={Stats}
          />
        );
      },
    },
    {
      id: 'clicks',
      header: t('Clicks'),
      size: 110,
      enableSorting: false,
      meta: { align: 'right' },
      cell({ row }: CellContext<Creative, unknown>): JSX.Element {
        return (
          <CreativeStat
            calculate={calculateClicks}
            creative={row.original}
            end={end}
            format={'count'}
            start={start}
            Stats={Stats}
          />
        );
      },
    },
    {
      id: 'ctr',
      header: t('CTR'),
      size: 110,
      enableSorting: false,
      meta: { align: 'right' },
      cell({ row }: CellContext<Creative, unknown>): JSX.Element {
        return (
          <CreativeStat
            calculate={calculateCtr}
            creative={row.original}
            end={end}
            format={'percent'}
            start={start}
            Stats={Stats}
          />
        );
      },
    },
    {
      id: 'is_enabled',
      accessorFn: (row): string => (row.get('is_enabled') ? t('Enabled') : t('Disabled')),
      header: t('Status'),
      size: 75,
      sortDescFirst: false,
      cell({ row }: CellContext<Creative, unknown>): JSX.Element {
        const enabled = row.original.get('is_enabled');
        return (
          <StatusPillCell
            status={enabled ? t('Enabled') : t('Disabled')}
            theme={enabled ? 'success' : 'neutral'}
          />
        );
      },
    },
    {
      id: 'destination_url',
      accessorFn: (row): string => getDestinationUrl(campaign, row),
      header: t('Destination URL'),
      sortDescFirst: false,
      cell({ row }: CellContext<Creative, unknown>): JSX.Element {
        const destinationURL = getDestinationUrl(campaign, row.original);
        return <TruncatedTextCell value={<a href={destinationURL}>{destinationURL}</a>} />;
      },
    },
  ];
}

export default CreativeTableColumns;
